import { Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { HttpClient } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import {
  AUDIT_FACET_FILTERS_ALL,
  AUDIT_LOG_DETAILS_ACTION_COLUMN,
  AUDIT_LOG_ROW_DETAIL_EXTRAS,
  auditLogDetailValue,
  buildAuditListQueryParams,
  mapAuditLogRowToDisplay,
  normalizeAuditListResponse,
} from './audit-log-datasource.utils';

export function audit_logs_all(
  componentDataSourceService: ComponentDataSourceService,
  http: HttpClient,
  _router: unknown
) {
  return {
    name: 'Audit Logs (All)',
    description: 'Complete audit log of all system changes',
    execute: (params?: LoadDataParams) => {
      const svc = componentDataSourceService as any;
      const request = svc['convertToListRequest'](
        { ...params, dataSourceKey: 'audit_logs_all' },
        'audit_logs_all'
      );
      request.includeFacets = true;
      const queryParams = buildAuditListQueryParams(request);

      return http
        .get<any>(`${componentDataSourceService.apiUrl}/audit-log`, { params: queryParams })
        .pipe(
          catchError((error) => {
            console.error('Error fetching audit logs:', error);
            return of({ items: [], totalCount: 0 });
          }),
          map((result: any) => {
            const norm = normalizeAuditListResponse(result, request.pageSize);
            if (!norm.items.length) {
              return {
                items: [],
                totalCount: norm.totalCount,
                pageSize: norm.pageSize,
                pageIndex: norm.pageIndex,
                facets: norm.facets ?? result?.facets,
              };
            }
            try {
              const auditLogs = norm.items.map((log: any) => mapAuditLogRowToDisplay(log));
              return {
                items: auditLogs,
                totalCount: norm.totalCount,
                pageSize: norm.pageSize,
                pageIndex: norm.pageIndex,
                facets: norm.facets ?? result?.facets,
              };
            } catch (error) {
              console.error('Error processing audit logs:', error);
              return { items: [], totalCount: 0, pageSize: norm.pageSize, pageIndex: norm.pageIndex };
            }
          })
        );
    },
    settings: {
      columns: [
        {
          key: 'changed_at',
          header: 'Hvornår',
          type: 'text',
          transform: (value: string) => {
            if (!value) {
              return 'N/A';
            }
            return new Date(value).toLocaleString('da-DK');
          },
          sortable: true,
          width: '180px',
        },
        {
          key: 'changed_by_name',
          header: 'Hvem (udført af)',
          type: 'text',
          sortable: true,
          width: '170px',
        },
        {
          key: 'affected_user_name',
          header: 'Berørt',
          type: 'text',
          transform: (name: string, row: any) => {
            if (!name || name === '-') {
              return '-';
            }
            if (row.affected_user_email) {
              return `${name} (${row.affected_user_email})`;
            }
            return name;
          },
          sortable: true,
          width: '220px',
        },
        {
          key: 'what_label',
          header: 'Hvad',
          type: 'text',
          sortable: true,
          width: '130px',
        },
        {
          key: 'field_label',
          header: 'Ændring',
          type: 'text',
          sortable: true,
          width: '200px',
        },
        {
          key: 'change_type_label',
          header: 'Handling',
          type: 'text',
          sortable: true,
          width: '110px',
        },
        {
          key: 'value_from_display',
          header: 'Fra',
          type: 'text',
          transform: (value: string) => {
            if (!value) {
              return '-';
            }
            if (value.length > 120) {
              return value.substring(0, 120) + '…';
            }
            return value;
          },
          detailTransform: auditLogDetailValue,
          width: '220px',
        },
        {
          key: 'value_to_display',
          header: 'Til',
          type: 'text',
          transform: (value: string) => {
            if (!value) {
              return '-';
            }
            if (value.length > 120) {
              return value.substring(0, 120) + '…';
            }
            return value;
          },
          detailTransform: auditLogDetailValue,
          width: '220px',
        },
        {
          key: 'notification_title',
          header: 'Notifikation',
          type: 'text',
          sortable: false,
          width: '160px',
        },
        AUDIT_LOG_DETAILS_ACTION_COLUMN,
      ],
      rowDetailExtras: AUDIT_LOG_ROW_DETAIL_EXTRAS,
      pageable: true,
      sortable: true,
      rowHover: true,
      searchable: true,
      defaultSortColumn: 'changed_at',
      defaultSortDirection: 'desc',
      facetFilters: AUDIT_FACET_FILTERS_ALL,
    },
  };
}
